import React, { useState, useEffect } from 'react';
import { couponService } from '../api/couponService';
import CouponFormModal from '../components/Coupons/CouponFormModal';
import CouponStatsModal from '../components/Coupons/CouponStatsModal';
import SearchBar from '../components/Common/SearchBar';
import Pagination from '../components/Common/Pagination';
import { Plus, Edit2, Trash2, BarChart2, Tag, TrendingUp, DollarSign, Users } from 'lucide-react';
import '../components/Common/Common.css';

function Coupons() {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [showFormModal, setShowFormModal] = useState(false);
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [selectedCoupon, setSelectedCoupon] = useState(null);
  const [selectedCouponId, setSelectedCouponId] = useState(null);
  const [statistics, setStatistics] = useState(null);

  const fetchCoupons = async (signal) => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        per_page: 15,
      };
      if (searchTerm && searchTerm.trim()) params.search = searchTerm.trim();
      if (statusFilter) params.status = statusFilter;
      if (typeFilter) params.type = typeFilter;

      const response = await couponService.getCoupons(params, signal);
      setCoupons(response.data);
      setLastPage(response.last_page || 1);
    } catch (error) {
      // Ignore aborts — they happen on rapid input
      if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') return;
      console.error('Error fetching coupons:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const data = await couponService.getGlobalStatistics();
      setStatistics(data);
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  };

  // Debounce + cancel in-flight requests to avoid hammering the API on each keystroke
  useEffect(() => {
    const controller = new AbortController();
    const debounceMs = searchTerm ? 400 : 0; // immediate on filter change, debounce on typing
    const timer = setTimeout(() => {
      fetchCoupons(controller.signal);
    }, debounceMs);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, searchTerm, statusFilter, typeFilter]);

  useEffect(() => {
    fetchStatistics();
  }, []);

  const handleCreate = () => {
    setSelectedCoupon(null);
    setShowFormModal(true);
  };

  const handleEdit = (coupon) => {
    setSelectedCoupon(coupon);
    setShowFormModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce coupon ?')) {
      try {
        await couponService.deleteCoupon(id);
        fetchCoupons();
        fetchStatistics();
      } catch (error) {
        console.error('Error deleting coupon:', error);
        alert('Échec de la suppression du coupon');
      }
    }
  };

  const handleViewStats = (coupon) => {
    setSelectedCouponId(coupon.id);
    setSelectedCoupon(coupon);
    setShowStatsModal(true);
  };

  const handleFormSubmit = async (formData) => {
    try {
      if (selectedCoupon) {
        await couponService.updateCoupon(selectedCoupon.id, formData);
      } else {
        await couponService.createCoupon(formData);
      }
      fetchCoupons();
      fetchStatistics();
      setShowFormModal(false);
    } catch (error) {
      throw error;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-MA', {
      style: 'currency',
      currency: 'MAD',
    }).format(amount || 0);
  };

  const isExpired = (expiryDate) => {
    if (!expiryDate) return false;
    return new Date(expiryDate) < new Date();
  };

  const getStatusBadge = (coupon) => {
    if (!coupon.is_active) {
      return <span className="badge badge-secondary">Inactif</span>;
    }
    if (isExpired(coupon.expires_at)) {
      return <span className="badge badge-danger">Expiré</span>;
    }
    return <span className="badge badge-success">Actif</span>;
  };

  const getValueDisplay = (coupon) => {
    if (coupon.type === 'percentage') {
      return `${coupon.value}%`;
    }
    return formatCurrency(coupon.value);
  };

  return (
    <div>
      <div className="page-header">
        <h1>Gestion des coupons</h1>
        <button onClick={handleCreate} className="btn-primary">
          <Plus size={20} style={{ marginRight: '8px' }} />
          Ajouter un coupon
        </button>
      </div>

      {/* Statistics Cards */}
      {statistics && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px', marginBottom: '24px' }}>
          <div className="stat-card">
            <div className="stat-icon" style={{ backgroundColor: '#dc143c' }}>
              <Tag size={28} />
            </div>
            <div className="stat-info">
              <div className="stat-label">Total coupons</div>
              <div className="stat-value">{statistics.total_coupons || 0}</div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon" style={{ backgroundColor: '#38a169' }}>
              <TrendingUp size={28} />
            </div>
            <div className="stat-info">
              <div className="stat-label">Coupons actifs</div>
              <div className="stat-value">{statistics.active_coupons || 0}</div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon" style={{ backgroundColor: '#805ad5' }}>
              <Users size={28} />
            </div>
            <div className="stat-info">
              <div className="stat-label">Total utilisations</div>
              <div className="stat-value">{statistics.total_uses || 0}</div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon" style={{ backgroundColor: '#e53e3e' }}>
              <DollarSign size={28} />
            </div>
            <div className="stat-info">
              <div className="stat-label">Remise totale</div>
              <div className="stat-value" style={{ fontSize: '22px' }}>{formatCurrency(statistics.total_discount)}</div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="filters">
        <div className="filters-grid">
          <div style={{ gridColumn: 'span 1' }}>
            <SearchBar
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder="Rechercher par code ou description..."
            />
          </div>

          <div>
            <label>Statut</label>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="">Tous les statuts</option>
              <option value="active">Actif</option>
              <option value="inactive">Inactif</option>
              <option value="expired">Expiré</option>
            </select>
          </div>

          <div>
            <label>Type</label>
            <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
              <option value="">Tous les types</option>
              <option value="percentage">Pourcentage</option>
              <option value="fixed">Montant fixe</option>
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="loading">Chargement des coupons...</div>
      ) : coupons.length === 0 ? (
        <div className="no-data">Aucun coupon trouvé</div>
      ) : (
        <>
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Type</th>
                  <th>Valeur</th>
                  <th>Min commande</th>
                  <th>Max utilisations</th>
                  <th>Utilisé</th>
                  <th>Statut</th>
                  <th>Expire le</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {coupons.map((coupon) => (
                  <tr key={coupon.id}>
                    <td>
                      <strong style={{ fontFamily: 'monospace', color: '#dc143c' }}>
                        {coupon.code}
                      </strong>
                      {coupon.product && (
                        <div style={{ fontSize: '11px', color: '#718096', marginTop: '2px' }}>
                          📦 {coupon.product.name}
                        </div>
                      )}
                    </td>
                    <td>
                      {coupon.type === 'percentage' ? (
                        <span className="badge badge-info">Pourcentage</span>
                      ) : (
                        <span className="badge badge-warning">Montant fixe</span>
                      )}
                    </td>
                    <td>
                      <strong>{getValueDisplay(coupon)}</strong>
                    </td>
                    <td>
                      {coupon.min_order_amount ? formatCurrency(coupon.min_order_amount) : '-'}
                    </td>
                    <td>
                      {coupon.max_uses || 'Illimité'}
                    </td>
                    <td>
                      <strong style={{ color: coupon.used_count > 0 ? '#38a169' : '#a0aec0' }}>
                        {coupon.used_count || 0}
                      </strong>
                    </td>
                    <td>{getStatusBadge(coupon)}</td>
                    <td>
                      {coupon.expires_at ? (
                        <span style={{ color: isExpired(coupon.expires_at) ? '#e53e3e' : '#2d3748' }}>
                          {formatDate(coupon.expires_at)}
                        </span>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          onClick={() => handleViewStats(coupon)}
                          className="btn-info"
                          title="Voir les statistiques"
                        >
                          <BarChart2 size={16} />
                        </button>
                        <button
                          onClick={() => handleEdit(coupon)}
                          className="btn-edit"
                          title="Modifier"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(coupon.id)}
                          className="btn-delete"
                          title="Supprimer"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Pagination
            currentPage={currentPage}
            lastPage={lastPage}
            onPageChange={setCurrentPage}
          />
        </>
      )}

      {/* Modals */}
      <CouponFormModal
        isOpen={showFormModal}
        onClose={() => setShowFormModal(false)}
        onSubmit={handleFormSubmit}
        coupon={selectedCoupon}
      />

      <CouponStatsModal
        isOpen={showStatsModal}
        onClose={() => setShowStatsModal(false)}
        couponId={selectedCouponId}
        couponCode={selectedCoupon?.code}
      />
    </div>
  );
}

export default Coupons;
